import { useQueryUtils } from "hooks";
import { createApiQuery } from "hooks/createApiQuery";
import { createPaginatedApiQuery } from "hooks/createPaginatedQuery";
import { useMutation } from "hooks/useMutation";
import { PartialOf } from "typeUtilities";
import { parsePatchData } from "utilities/parsePatchData";
import {
  getManufacturingSchemaStages,
  getProductionPlan,
  getProductionPlans,
  getProductionPlanSchedule,
  getSchemaStages,
  getShelfItemDetails,
  getShelfGroups,
  patchProductionPlanOnDetails,
  patchProductionPlanOnList,
} from "./calls";
import { productionPlansKeys } from "./keys";
import { ProductionPlan, ProductionPlanListItem } from "./models";

export const useProductionPlans = createPaginatedApiQuery(getProductionPlans);
export const useProductionPlan = createApiQuery(getProductionPlan);

export const useProductionPlanSchedule = createApiQuery(getProductionPlanSchedule);

export const useManufacturingSchemaStages = createPaginatedApiQuery(getManufacturingSchemaStages);
export const useSchemaStages = createPaginatedApiQuery(getSchemaStages);

export const useShelfItems = createApiQuery(getShelfGroups);
export const useShelfItemDetails = createPaginatedApiQuery(getShelfItemDetails);

export const useProductionPlanOnListPatchMutation = (parseData: boolean = false) => {
  const { rollback, handlePaginatedListUpdate, rollbackList } = useQueryUtils();

  return useMutation(
    ({ id, toUpdate }: { id: string; toUpdate: PartialOf<ProductionPlanListItem> }) => {
      if (parseData) {
        return patchProductionPlanOnList(parsePatchData(toUpdate), id);
      }
      return patchProductionPlanOnList(toUpdate, id);
    },
    {
      onMutate: ({ id, toUpdate }) => {
        const prevList = handlePaginatedListUpdate(
          productionPlansKeys.productionPlans(),
          id,
          toUpdate,
        );

        return { prevList };
      },
      onError: (error, { id }, onMutateReturn) => {
        const { prevList } = onMutateReturn as { prevList: any };
        rollback(productionPlansKeys.productionPlan(String(id)), error);
        rollbackList(productionPlansKeys.productionPlans(), prevList, id);
      },
    },
  );
};

export const useProductionPlanDetailsPatchMutation = () => {
  const { handleMutate, rollback } = useQueryUtils();

  return useMutation(
    ({ id, toUpdate }: { id: string | number; toUpdate: PartialOf<ProductionPlan> }) =>
      patchProductionPlanOnDetails(parsePatchData(toUpdate), id),
    {
      onMutate: ({ id, toUpdate }) => {
        const prevDetails = handleMutate(
          productionPlansKeys.productionPlanSchedule(String(id)),
          toUpdate,
        );
        return { prevDetails };
      },
      onError: (error, { id }, onMutateReturn) => {
        const { prevDetails } = onMutateReturn as { prevDetails: ProductionPlan };
        rollback(productionPlansKeys.productionPlanSchedule(String(id)), prevDetails, error);
      },
    },
  );
};
